import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const ProjetosContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-dark);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  background: var(--color-dark-gray);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: 1;
  }
`;

const ProjectImageContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 5rem;
  background-color: rgba(255, 144, 82, 0.1);
`;

const ProjectBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--color-primary);
  color: var(--color-white);
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ProjectTitle = styled.h3`
  color: var(--color-white);
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const ProjectDescription = styled.p`
  color: var(--color-white);
  opacity: 0.7;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
`;

const TechTag = styled.span`
  background: rgba(255, 144, 82, 0.2);
  color: var(--color-white);
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  border: 1px solid rgba(255, 144, 82, 0.3);
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ProjectLink = styled(motion.a)`
  background: rgba(255, 144, 82, 0.2);
  color: var(--color-white);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.9rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 144, 82, 0.3);

  &:hover {
    background: rgba(255, 144, 82, 0.3);
  }
`;

const ProjectFilters = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 800px;
`;

const FilterButton = styled(motion.button)<{ active: boolean }>`
  background: ${(props) =>
    props.active ? "rgba(255, 144, 82, 0.3)" : "rgba(26, 26, 26, 0.7)"};
  color: var(--color-white);
  padding: 0.5rem 1.2rem;
  border-radius: 50px;
  border: 1px solid
    ${(props) =>
      props.active ? "var(--color-primary)" : "rgba(255, 144, 82, 0.2)"};
  cursor: pointer;
  font-size: 1rem;
  box-shadow: ${(props) =>
    props.active ? "0 0 15px rgba(255, 144, 82, 0.3)" : "none"};
`;

const BackButton = styled(motion.button)`
  background: rgba(26, 26, 26, 0.7);
  color: var(--color-white);
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }
`;

const Projetos: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [activeFilter, setActiveFilter] = React.useState("all");

  const filters = [
    { id: "all", label: "All" },
    { id: "game", label: "Games" },
    { id: "web", label: "Web" },
    { id: "mobile", label: "Mobile" },
    { id: "other", label: "Others" },
  ];

  const projects = [
    {
      title: "🍸 Speakeit Ease: Spirits Saga",
      description:
        "An AI-driven bartender arcade game where each drink is uniquely crafted based on in-game ingredients, offering personalized experiences for every player.",
      icon: "https://static-00.iconduck.com/assets.00/itch-io-icon-2048x2048-i6hzclad.png",
      type: "game",
      badge: "Personal",
      technologies: ["Unity", "C#", "FMOD"],
      links: [
        {
          label: "Demo Browser",
          url: "https://caiquecg.itch.io/speakeasy-spirits-saga",
          icon: "▶️",
        },
      ],
    },
    {
      title: "🎴 White-label Triple Triad Card",
      description:
        "A procedurally generated card game inspired by Triple Triad, where unique cards are created based on scanned QR codes, ensuring consistent and non-repetitive gameplay.",
      icon: "https://static-00.iconduck.com/assets.00/itch-io-icon-2048x2048-i6hzclad.png",
      type: "game",
      badge: "Personal",
      technologies: ["Unity", "C#", "Blender", "FMOD"],
      links: [
        {
          label: "Demo Whitelabel",
          url: "https://caiquecg.itch.io/triple-triad-card-game",
          icon: "▶️",
        },
      ],
    },
    {
      title: "ExitLag Portal",
      description:
        "Developed a high-performance web platform for optimizing online gaming connections, reducing latency, and enhancing user experience globally.",
      icon: "https://files.inhire.app/og-images/exitlag.png",
      type: "web",
      badge: "Professional",
      technologies: ["C++", "Node.js", "React", "Redis"],
      links: [{ label: "Website", url: "https://exitlag.com/", icon: "🌐" }],
    },
    {
      title: "AutoEsporte - Editora Globo",
      description:
        "Contributed to the development and maintenance of AutoEsporte's digital platform, delivering automotive news and reviews to a broad audience.",
      icon: "https://s2-autoesporte.glbimg.com/FHyXmgkegYJEc5CKRyHopNaz_Io=/0x0:419x198/984x0/smart/filters:strip_icc()/i.s3.glbimg.com/v1/AUTH_cf9d035bf26b4646b105bd958f32089d/internal_photos/bs/2020/B/d/WoClxOTpmzfrQavh7ySw/2014-07-28-logoae12.jpg",
      type: "web",
      badge: "Professional",
      technologies: ["React", "Node.js", "GraphQL"],
      links: [
        { label: "Website", url: "https://autoesporte.globo.com/", icon: "🌐" },
      ],
    },
    {
      title: "Valor Econômico - Editora Globo",
      description:
        "Enhanced the financial news portal by implementing scalable front-end solutions, improving performance and user engagement.",
      icon: "https://logodownload.org/wp-content/uploads/2019/12/valor-economico-logo-0.png",
      type: "web",
      badge: "Professional",
      technologies: ["React", "Next.js", "TypeScript"],
      links: [
        { label: "Website", url: "https://valor.globo.com/", icon: "🌐" },
      ],
    },
    {
      title: "O Globo - Editora Globo",
      description:
        "Played a key role in modernizing the O Globo news website, focusing on responsive design and content delivery optimization.",
      icon: "https://d37iydjzbdkvr9.cloudfront.net/google-assistant/o-globo/logo-globo-1000x1000.jpg",
      type: "web",
      badge: "Professional",
      technologies: ["Vue.js", "Nuxt.js", "Node.js"],
      links: [
        { label: "Website", url: "https://oglobo.globo.com/", icon: "🌐" },
      ],
    },
    {
      title: "BackStage - Globo.com",
      description:
        "Developed internal tools to enhance developer experience and implemented micro-frontend architectures for scalable applications.",
      icon: "https://upload.wikimedia.org/wikipedia/commons/2/27/Logo_do_provedor_Globo.png",
      type: "web",
      badge: "Professional",
      technologies: [
        "React",
        "Micro-Frontends",
        "Docker",
        "Redis",
        "AWS",
        "GraphQL",
        "Golang",
        "Python",
        "Jinja2",
        "Java",
      ],
      links: [{ label: "Internal Tool", url: "#", icon: "🔧" }],
    },
    {
      title: "Tokyo 2020 Olympics Coverage in globoesporte.com",
      description:
        "Engineered scalable solutions to support real-time content delivery and high traffic volumes during the Tokyo 2020 Olympic Games.",
      icon: "https://i.pinimg.com/564x/85/17/89/851789b1f0d3f0b34e19eeb116412b4a.jpg",
      type: "web",
      badge: "Professional",
      technologies: ["Node.js", "React", "Redis", "Docker", "AWS"],
      links: [
        {
          label: "Event Site",
          url: "https://ge.globo.com/olimpiadas/",
          icon: "🌐",
        },
      ],
    },
    {
      title: "PIX - Instant Payment System",
      description:
        "Contributed to the development of Brazil's instant payment system, ensuring secure and immediate financial transactions nationwide.",
      icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/Logo%E2%80%94pix_powered_by_Banco_Central_%28Brazil%2C_2020%29.svg/2560px-Logo%E2%80%94pix_powered_by_Banco_Central_%28Brazil%2C_2020%29.svg.png",
      type: "web",
      badge: "Professional",
      technologies: [
        "Kotlin",
        "Spring Boot",
        "PostgreSQL",
        "Redis",
        "Docker",
        "AWS",
        "Kafka",
      ],
      links: [
        {
          label: "Official Info",
          url: "https://www.bcb.gov.br/estabilidadefinanceira/pix",
          icon: "🌐",
        },
      ],
    },
    {
      title: "PagSeguro Recurring Payments",
      description:
        "Implemented subscription-based payment solutions, enhancing revenue streams and user retention for e-commerce platforms.",
      icon: "https://cdn.worldvectorlogo.com/logos/pagbank-2.svg",
      type: "web",
      badge: "Professional",
      technologies: ["React", "Nodejs", "PostgreSQL", "Redis", "Docker", "AWS"],
      links: [
        {
          label: "Service Page",
          url: "https://pagbank.com.br/para-seu-negocio/online-integracao/pagamento-recorrente",
          icon: "🌐",
        },
      ],
    },
    {
      title: "Jostens E-Commerce Platform",
      description:
        "Developed a robust e-commerce platform for Jostens, facilitating seamless online shopping experiences for customers.",
      icon: "https://findlogovector.com/wp-content/uploads/2018/09/jostens-logo-vector.png",
      type: "web",
      badge: "Professional",
      technologies: ["AWS", "React.js", "Next.js", "MySQL"],
      links: [
        { label: "Website", url: "https://www.jostens.com/", icon: "🌐" },
      ],
    },
    {
      title: "Globo Experience App",
      description:
        "React Native mobile app designed to enhance the immersive experience of visitors to Globo's interactive theme space, Gexperience. Acted as Technical Lead and developer, overseeing architecture design, cross-platform implementation, headless CMS integration, and seamless multimedia functionalities, including personalized media content delivery and ticket purchasing systems.",
      icon: "https://www.gexperience.com.br/assets/images/redirect/gexperience_logo.svg",
      type: "mobile",
      badge: "Professional",
      technologies: [
        "React Native",
        "Expo",
        "GraphQL",
        "WordPress Headless",
        "AWS",
      ],
      links: [
        { label: "Website", url: "https://gexperience.globo.com/", icon: "🌐" },
        { label: "App Store", url: "#", icon: "🍎" },
        { label: "Google Play", url: "#", icon: "🤖" },
      ],
    },
    {
      title: "Liberty Furniture E-Commerce",
      description:
        "Built and maintained an e-commerce site for Liberty Furniture, enhancing product visibility and customer engagement.",
      icon: "https://m.media-amazon.com/images/S/abs-image-upload-na/4/AmazonStores/ATVPDKIKX0DER/52624ad3176e1c7e5f504f5aa0e05602.w2000.h2000.jpg",
      type: "web",
      badge: "Professional",
      technologies: ["AWS", "React.js", "Next.js", "MySQL", "Node.js", "GCP"],
      links: [
        {
          label: "Website",
          url: "https://www.mylibertyfurniture.com/",
          icon: "🌐",
        },
      ],
    },
    {
      title: "Nextel Portal - Click to Call Feature",
      description:
        "Developed the 'Click to Call' feature for Nextel's portal, significantly boosting user interaction and contributing to 50% of the company's quarterly revenue.",
      icon: "https://logodownload.org/wp-content/uploads/2018/09/nextel-logo-3.png",
      type: "web",
      badge: "Professional",
      technologies: ["React", ".NetCore", "Node.js", "AWS", "Redis"],
      links: [
        { label: "Nextel Bought by Claro Telecom", url: "#", icon: "📄" },
      ],
    },
    {
      title: "Nextel URA System",
      description:
        "Engineered the URA (Unidade de Resposta Audível) system for Nextel, improving customer service efficiency through automated voice responses.",
      icon: "https://logodownload.org/wp-content/uploads/2018/09/nextel-logo-3.png",
      type: "other",
      badge: "Professional",
      technologies: ["Node.js", "MySQL", "MongoDB", "Redis", "AWS"],
      links: [{ label: "System Details", url: "#", icon: "📄" }],
    },
    {
      title: "Nextel FAQ System",
      description:
        "Developed a comprehensive FAQ system for Nextel, enhancing user support and reducing customer service inquiries.",
      icon: "https://logodownload.org/wp-content/uploads/2018/09/nextel-logo-3.png",
      type: "web",
      badge: "Professional",
      technologies: [
        "React",
        "Node.js",
        "MySQL",
        "ZenDesk API",
        "Redis",
        "AWS",
      ],
      links: [
        { label: "Nextel Bought by Claro Telecom", url: "#", icon: "🌐" },
      ],
    },
    {
      title: "Seu Oswaldo – Financial Management App",
      description:
        "Developed a React Native mobile application tailored for financial management in the third sector. The app streamlines budgeting, expense tracking, and financial reporting, empowering non-profit organizations to manage their finances efficiently.",
      icon: "https://portalv3-front-images-dev.s3.amazonaws.com/images/assertiva/assertiva-logos_logo-cor-full.png",
      type: "mobile",
      badge: "Professional",
      technologies: ["React Native", "TypeScript", "Firebase"],
      links: [{ label: "Project Overview", url: "#", icon: "📄" }],
    },
    {
      title: "Assertiva CRM Platform",
      description:
        "Led the development of a comprehensive CRM platform for Assertiva, encompassing the entire sales pipeline. Built from scratch within a 12-day timeframe, the project involved full-stack development, team leadership, and developer mentorship, resulting in a robust solution that enhanced client relationship management.",
      icon: "https://portalv3-front-images-dev.s3.amazonaws.com/images/assertiva/assertiva-logos_logo-cor-full.png",
      type: "web",
      badge: "Professional",
      technologies: ["React", "Node.js", "Express", "MongoDB"],
      links: [{ label: "Case Study", url: "#", icon: "📝" }],
    },
  ];

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((project) => project.type === activeFilter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <ProjetosContainer>
      <Title
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Projects
      </Title>

      <ProjectFilters>
        {filters.map((filter) => (
          <FilterButton
            key={filter.id}
            active={activeFilter === filter.id}
            onClick={() => setActiveFilter(filter.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {filter.label}
          </FilterButton>
        ))}
      </ProjectFilters>

      <ProjectsGrid
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredProjects.map((project, index) => (
          <ProjectCard key={index} variants={itemVariants}>
            <ProjectImage>
              <ProjectImageContent>
                <img
                  src={project.icon}
                  alt={project.title}
                  style={{
                    width: "75%",
                    height: "75%",
                    objectFit: "fill",
                    borderRadius: "15px",
                    overflow: "hidden",
                    zIndex: 2,
                  }}
                />
              </ProjectImageContent>
              <ProjectBadge>{project.badge}</ProjectBadge>
            </ProjectImage>
            <ProjectContent>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <ProjectTech>
                {project.technologies.map((tech, i) => (
                  <TechTag key={i}>{tech}</TechTag>
                ))}
              </ProjectTech>
              <ProjectLinks>
                {project.links.map((link, i) => (
                  <ProjectLink
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {link.icon} {link.label}
                  </ProjectLink>
                ))}
              </ProjectLinks>
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>

      <BackButton
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Voltar ao Menu
      </BackButton>
    </ProjetosContainer>
  );
};

export default Projetos;
